/**
 * useUserCaps
 * ───────────
 * React-Hook, der die **effektiven Capabilities des eingeloggten Users**
 * liefert (Rollen-Defaults ∪ permissions_override.grant \ revoke).
 *
 * Anders als `useTenantCapabilities()` (Mandanten-Feature-Flags) prüft dieser
 * Hook rollenbasierte Nutzer-Rechte gegen `capabilities.py`.
 *
 * Nutzung
 * -------
 *   const { hasCap, loaded } = useUserCaps();
 *   if (hasCap("see_intake_pipeline")) { … }
 *
 * Der Katalog wird beim ersten Aufruf geladen und modul-weit gecached — damit
 * belastet der Hook das UI nur einmal pro Session.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

let _cache = null; // { role_defaults: {role: [caps]}, capabilities: {cap: label} }
let _pending = null;

async function _loadCatalog() {
  if (_cache) return _cache;
  if (!_pending) {
    _pending = api.get("/capabilities")
      .then((r) => { _cache = r.data || {}; return _cache; })
      .catch(() => { _cache = { role_defaults: {}, capabilities: {} }; return _cache; })
      .finally(() => { _pending = null; });
  }
  return _pending;
}

export function useUserCaps() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(_cache);

  useEffect(() => {
    if (!user) return;
    if (_cache) { setCatalog(_cache); return; }
    let alive = true;
    _loadCatalog().then((c) => { if (alive) setCatalog(c); });
    return () => { alive = false; };
  }, [user]);

  const hasCap = useCallback((cap) => {
    if (!user) return false;
    // Super-Admin-Bypass — hat immer alle Rechte
    if (user.role === "super_admin" || user.is_super_admin) return true;
    // Wenn Katalog noch nicht geladen ist, fallback: rollenbasiert erlauben,
    // um kurzes Flackern beim ersten Render zu vermeiden.
    const defaults = catalog?.role_defaults?.[user.role] || [];
    const base = new Set(defaults);
    const override = user.permissions_override || {};
    (override.grant || []).forEach((c) => base.add(c));
    (override.revoke || []).forEach((c) => base.delete(c));
    return base.has(cap);
  }, [user, catalog]);

  return { hasCap, loaded: !!catalog };
}
